import { ExtendCurrency } from 'src/enums'
import {
  TransactionFinalApprovalStatus,
  TransactionManagerRejectionReason,
  TransactionManagerReviewStatus,
} from 'src/models/transaction.models'
import {
  ConversationMessagePostedPayload,
  ExpenseCategoriesUpdatedPayload,
  ExpenseCategorySelection,
  ExpenseCustomFieldChange,
  ExpenseCustomFieldsUpdatedPayload,
  FinalReviewUpdatedPayload,
  TransactionActivity,
} from 'src/queries/transactionActivities/transactionActivities.models'
import validateCurrencyCode from 'validate-currency-code'

export type TransactionActivityIcon =
  | 'authorized'
  | 'declined'
  | 'settled'
  | 'reversed'
  | 'receipt'
  | 'updated'
  | 'approval'
  | 'unknown'

type LocalizedTextSource =
  | 'Activity updated'
  | 'Spent'
  | 'Transaction was declined'
  | 'Transaction cleared'
  | 'Transaction was reversed'
  | 'Automatically matched a receipt'
  | 'Matched receipt to this transaction'
  | 'Sent a receipt reminder'
  | 'Attached a receipt'
  | 'Moved receipt'
  | 'Removed receipt'
  | 'Updated Notes'
  | 'Removed Notes'
  | 'Comment'
  | 'Left a comment'
  | 'Comment unavailable'
  | 'Updated expense category'
  | 'Updated expense categories'
  | 'Updated ${categoryName} expense category:'
  | 'Updated expense fields'
  | 'Updated ${fieldName}:'
  | 'Split ${splitNumber}'
  | 'Updated manager review:'
  | 'Updated final review:'
  | 'Rejected transaction'
  | 'Removed rejection note'
  | 'Auto-reviewed'
  | 'Reviewed'
  | 'Rejected'
  | 'Pending Review'
  | 'Receipt Missing'
  | 'Receipt Incorrect'
  | 'Expense Category Missing'
  | 'Expense Category Incorrect'
  | 'Note/Justification Missing'
  | 'Not a business expense'
  | 'Other'
  | 'Flagged'
  | 'Pending Approval'
  | 'Approved'

type InterpolatedLocalizedTextSource =
  | 'Updated ${categoryName} expense category:'
  | 'Updated ${fieldName}:'
  | 'Split ${splitNumber}'

type PlainLocalizedTextSource = Exclude<
  LocalizedTextSource,
  InterpolatedLocalizedTextSource
>

export type LocalizedText =
  | { source: PlainLocalizedTextSource }
  | {
      source: 'Updated ${categoryName} expense category:'
      values: { categoryName: string }
    }
  | { source: 'Updated ${fieldName}:'; values: { fieldName: string } }
  | { source: 'Split ${splitNumber}'; values: { splitNumber: string } }

export type TransactionActivityDetail =
  | { kind: 'quote'; value: string; truncated: boolean }
  | { kind: 'comment'; value: string; truncated: boolean }
  | {
      kind: 'change'
      label?: LocalizedText
      before: string | null
      after: string | null
    }
  | { kind: 'status'; value: LocalizedText }

interface TransactionActivityMoney {
  amountCents: number
  currency: ExtendCurrency
}

export interface TransactionActivityPresentation {
  title: LocalizedText
  icon: TransactionActivityIcon
  supported: boolean
  details: TransactionActivityDetail[]
  money?: TransactionActivityMoney
}

const LIFECYCLE_PRESENTATIONS = {
  'transaction.authorized': {
    title: 'Spent',
    icon: 'authorized',
    status: 'AUTHORIZED',
  },
  'transaction.declined': {
    title: 'Transaction was declined',
    icon: 'declined',
    status: 'DECLINED',
  },
  'transaction.settled': {
    title: 'Transaction cleared',
    icon: 'settled',
    status: 'SETTLED',
  },
  'transaction.reversed': {
    title: 'Transaction was reversed',
    icon: 'reversed',
    status: 'REVERSED',
  },
} as const

type LifecycleType = keyof typeof LIFECYCLE_PRESENTATIONS

type CategoryChangeGroup = {
  categoryName: string | null
  splitIndex: number | null
  before: string[]
  after: string[]
}

const localized = (source: PlainLocalizedTextSource): LocalizedText => ({
  source,
})

const localizedCategoryName = (categoryName: string): LocalizedText => ({
  source: 'Updated ${categoryName} expense category:',
  values: { categoryName },
})

const localizedFieldName = (fieldName: string): LocalizedText => ({
  source: 'Updated ${fieldName}:',
  values: { fieldName },
})

const localizedSplitNumber = (splitNumber: string): LocalizedText => ({
  source: 'Split ${splitNumber}',
  values: { splitNumber },
})

const genericPresentation = (): TransactionActivityPresentation => ({
  title: localized('Activity updated'),
  icon: 'unknown',
  supported: false,
  details: [],
})

const simplePresentation = (
  title: PlainLocalizedTextSource,
  icon: TransactionActivityIcon,
): TransactionActivityPresentation => ({
  title: localized(title),
  icon,
  supported: true,
  details: [],
})

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const isLifecycleType = (value: string): value is LifecycleType =>
  value in LIFECYCLE_PRESENTATIONS

const isNullableString = (value: unknown): value is string | null =>
  typeof value === 'string' || value === null

const isDisplayString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0

const getMoney = (
  payload: Record<string, unknown>,
): TransactionActivityMoney | undefined => {
  const { amountCents, currency } = payload

  if (
    typeof amountCents !== 'number' ||
    !Number.isFinite(amountCents) ||
    typeof currency !== 'string' ||
    !validateCurrencyCode(currency)
  ) {
    return undefined
  }

  return {
    amountCents,
    currency: currency as ExtendCurrency,
  }
}

const getNotePresentation = (
  payload: Record<string, unknown>,
): TransactionActivityPresentation | undefined => {
  const { change, afterDisplayValue } = payload
  const truncated = payload.truncated ?? false

  if (change !== 'ADDED' && change !== 'UPDATED' && change !== 'REMOVED') {
    return undefined
  }

  if (typeof truncated !== 'boolean') {
    return simplePresentation('Updated Notes', 'updated')
  }

  if (change === 'REMOVED') {
    return simplePresentation('Removed Notes', 'updated')
  }

  if (!isDisplayString(afterDisplayValue)) {
    return simplePresentation('Updated Notes', 'updated')
  }

  return {
    title: localized('Updated Notes'),
    icon: 'updated',
    supported: true,
    details: [
      {
        kind: 'quote',
        value: afterDisplayValue,
        truncated,
      },
    ],
  }
}

const parseConversationMessagePostedPayload = (
  payload: Record<string, unknown>,
): ConversationMessagePostedPayload | undefined => {
  if (!isDisplayString(payload.messageId)) {
    return undefined
  }

  if (
    (payload.state !== 'ACTIVE' &&
      payload.state !== 'DELETED' &&
      payload.state !== 'UNAVAILABLE') ||
    !(
      payload.body === undefined ||
      payload.body === null ||
      typeof payload.body === 'string'
    )
  ) {
    return undefined
  }

  return {
    messageId: payload.messageId,
    state: payload.state,
    ...(typeof payload.body === 'string' ? { body: payload.body } : {}),
  }
}

const getConversationMessagePresentation = (
  payload: Record<string, unknown>,
): TransactionActivityPresentation => {
  const conversationPayload = parseConversationMessagePostedPayload(payload)

  if (
    conversationPayload?.state === 'ACTIVE' &&
    isDisplayString(conversationPayload.body)
  ) {
    return {
      title: localized('Left a comment'),
      icon: 'updated',
      supported: true,
      details: [
        {
          kind: 'quote',
          value: conversationPayload.body,
          truncated: false,
        },
      ],
    }
  }

  return simplePresentation('Comment unavailable', 'updated')
}

const parseExpenseCategorySelection = (
  value: unknown,
): ExpenseCategorySelection | undefined => {
  const categoryName =
    isObject(value) && 'categoryName' in value ? value.categoryName : null

  if (
    !isObject(value) ||
    !isNullableString(value.categoryId) ||
    !isNullableString(value.labelId) ||
    !isNullableString(value.label) ||
    !isNullableString(value.freeText) ||
    !isNullableString(value.splitId) ||
    !(typeof value.splitIndex === 'number' || value.splitIndex === null) ||
    typeof value.generatedByPrediction !== 'boolean'
  ) {
    return undefined
  }

  if (!isNullableString(categoryName)) {
    return undefined
  }

  return {
    categoryId: value.categoryId,
    categoryName,
    labelId: value.labelId,
    label: value.label,
    freeText: value.freeText,
    splitId: value.splitId,
    splitIndex: value.splitIndex,
    generatedByPrediction: value.generatedByPrediction,
  }
}

const parseCategorySelections = (
  value: unknown,
): ExpenseCategorySelection[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined
  }

  const selections = value.map(parseExpenseCategorySelection)
  return selections.every(Boolean)
    ? (selections as ExpenseCategorySelection[])
    : undefined
}

const categoryEntryKey = (entry: ExpenseCategorySelection): string =>
  JSON.stringify([entry.splitId, entry.categoryId])

const sortCategoryEntries = (
  entries: ExpenseCategorySelection[],
): ExpenseCategorySelection[] =>
  [...entries].sort((left, right) => {
    const leftKey = [
      left.splitIndex ?? -1,
      left.splitId ?? '',
      left.categoryId ?? '',
      left.labelId ?? '',
      left.label ?? '',
      left.freeText ?? '',
    ]
    const rightKey = [
      right.splitIndex ?? -1,
      right.splitId ?? '',
      right.categoryId ?? '',
      right.labelId ?? '',
      right.label ?? '',
      right.freeText ?? '',
    ]

    return leftKey.join('\u0000').localeCompare(rightKey.join('\u0000'))
  })

const displayCategoryValue = (
  entry: ExpenseCategorySelection,
): string | undefined =>
  isDisplayString(entry.freeText)
    ? entry.freeText
    : isDisplayString(entry.label)
      ? entry.label
      : undefined

const getCategoryPresentations = (
  payload: Record<string, unknown>,
): TransactionActivityPresentation | undefined => {
  const before = parseCategorySelections(payload.before)
  const after = parseCategorySelections(payload.after)

  if (!before || !after) {
    return undefined
  }

  const categoryPayload: ExpenseCategoriesUpdatedPayload = { before, after }
  const groups = new Map<string, CategoryChangeGroup>()

  const addEntry = (
    entry: ExpenseCategorySelection,
    side: 'before' | 'after',
  ): void => {
    const key = categoryEntryKey(entry)
    const group = groups.get(key) ?? {
      categoryName: null,
      splitIndex: entry.splitIndex,
      before: [],
      after: [],
    }
    const value = displayCategoryValue(entry)

    group.categoryName = group.categoryName ?? entry.categoryName ?? null
    group.splitIndex = group.splitIndex ?? entry.splitIndex
    if (value) {
      group[side].push(value)
    }
    groups.set(key, group)
  }

  sortCategoryEntries(categoryPayload.before).forEach((entry) =>
    addEntry(entry, 'before'),
  )
  sortCategoryEntries(categoryPayload.after).forEach((entry) =>
    addEntry(entry, 'after'),
  )

  if (groups.size === 0) {
    return simplePresentation('Updated expense category', 'updated')
  }

  const orderedGroups = [...groups.values()].sort(
    (left, right) => (left.splitIndex ?? -1) - (right.splitIndex ?? -1),
  )
  const categoryName = orderedGroups[0]?.categoryName
  const title =
    orderedGroups.length === 1 && categoryName
      ? localizedCategoryName(categoryName)
      : localized(
          orderedGroups.length === 1
            ? 'Updated expense category'
            : 'Updated expense categories',
        )

  return {
    title,
    icon: 'updated',
    supported: true,
    details: orderedGroups.map((group) => ({
      kind: 'change',
      ...(group.splitIndex === null
        ? {}
        : {
            label: localizedSplitNumber(String(group.splitIndex + 1)),
          }),
      before: group.before.length ? group.before.join(', ') : null,
      after: group.after.length ? group.after.join(', ') : null,
    })),
  }
}

const parseExpenseCustomFieldChange = (
  value: unknown,
): ExpenseCustomFieldChange | undefined => {
  if (
    !isObject(value) ||
    typeof value.fieldId !== 'string' ||
    !isNullableString(value.fieldName) ||
    !isNullableString(value.beforeDisplayValue) ||
    !isNullableString(value.afterDisplayValue)
  ) {
    return undefined
  }

  return {
    fieldId: value.fieldId,
    fieldName: value.fieldName,
    beforeDisplayValue: value.beforeDisplayValue,
    afterDisplayValue: value.afterDisplayValue,
  }
}

const getCustomFieldPresentations = (
  payload: Record<string, unknown>,
): TransactionActivityPresentation | undefined => {
  if (!Array.isArray(payload.changes)) {
    return undefined
  }

  const changes = payload.changes.map(parseExpenseCustomFieldChange)
  if (changes.length === 0 || !changes.every(Boolean)) {
    return undefined
  }

  const customFieldPayload: ExpenseCustomFieldsUpdatedPayload = {
    changes: changes as ExpenseCustomFieldChange[],
  }
  const singularChange =
    customFieldPayload.changes.length === 1
      ? customFieldPayload.changes[0]
      : undefined
  const title =
    singularChange && isDisplayString(singularChange.fieldName)
      ? localizedFieldName(singularChange.fieldName)
      : localized('Updated expense fields')

  return {
    title,
    icon: 'updated',
    supported: true,
    details: customFieldPayload.changes.map((change) => ({
      kind: 'change',
      ...(singularChange || !isDisplayString(change.fieldName)
        ? {}
        : {
            label: localizedFieldName(change.fieldName),
          }),
      before: isDisplayString(change.beforeDisplayValue)
        ? change.beforeDisplayValue
        : null,
      after: isDisplayString(change.afterDisplayValue)
        ? change.afterDisplayValue
        : null,
    })),
  }
}

const isManagerReviewStatus = (
  value: unknown,
): value is TransactionManagerReviewStatus =>
  Object.values(TransactionManagerReviewStatus).includes(
    value as TransactionManagerReviewStatus,
  )

const isManagerRejectionReason = (
  value: unknown,
): value is TransactionManagerRejectionReason =>
  Object.values(TransactionManagerRejectionReason).includes(
    value as TransactionManagerRejectionReason,
  )

const MANAGER_REVIEW_STATUS_TEXT: Record<
  TransactionManagerReviewStatus,
  LocalizedText
> = {
  [TransactionManagerReviewStatus.AutoReviewed]: localized('Auto-reviewed'),
  [TransactionManagerReviewStatus.Reviewed]: localized('Approved'),
  [TransactionManagerReviewStatus.Rejected]: localized('Rejected'),
  [TransactionManagerReviewStatus.PendingReview]: localized('Pending Review'),
}

const getManagerReviewPresentation = (
  payload: Record<string, unknown>,
): TransactionActivityPresentation | undefined => {
  if (
    !isManagerReviewStatus(payload.before) ||
    !isManagerReviewStatus(payload.after) ||
    !(
      payload.rejectionReason === null ||
      payload.rejectionReason === undefined ||
      isManagerRejectionReason(payload.rejectionReason)
    ) ||
    !(
      payload.rejectionNote === null ||
      payload.rejectionNote === undefined ||
      isNullableString(payload.rejectionNote)
    ) ||
    !(
      payload.rejectionNoteTruncated === undefined ||
      typeof payload.rejectionNoteTruncated === 'boolean'
    ) ||
    !(
      payload.rejectionNoteChanged === undefined ||
      typeof payload.rejectionNoteChanged === 'boolean'
    )
  ) {
    return undefined
  }

  const managerPayload = {
    before: payload.before,
    after: payload.after,
    rejectionNote: isDisplayString(payload.rejectionNote)
      ? payload.rejectionNote
      : null,
    rejectionNoteTruncated: payload.rejectionNoteTruncated === true,
    rejectionNoteChanged: payload.rejectionNoteChanged === true,
  }

  if (
    managerPayload.before !== TransactionManagerReviewStatus.Rejected &&
    managerPayload.after === TransactionManagerReviewStatus.Rejected
  ) {
    const details: TransactionActivityDetail[] = [
      ...(managerPayload.rejectionNote
        ? [
            {
              kind: 'comment' as const,
              value: managerPayload.rejectionNote,
              truncated: managerPayload.rejectionNoteTruncated,
            },
          ]
        : []),
    ]

    return {
      title: localized('Rejected transaction'),
      icon: 'approval',
      supported: true,
      details,
    }
  }

  return {
    title: localized('Updated manager review:'),
    icon: 'approval',
    supported: true,
    details: [
      {
        kind: 'status',
        value:
          MANAGER_REVIEW_STATUS_TEXT[
            managerPayload.after as TransactionManagerReviewStatus
          ],
      },
      ...(managerPayload.before === TransactionManagerReviewStatus.Rejected &&
      managerPayload.after === TransactionManagerReviewStatus.Rejected &&
      managerPayload.rejectionNoteChanged
        ? managerPayload.rejectionNote
          ? [
              {
                kind: 'comment' as const,
                value: managerPayload.rejectionNote,
                truncated: managerPayload.rejectionNoteTruncated,
              },
            ]
          : [
              {
                kind: 'status' as const,
                value: localized('Removed rejection note'),
              },
            ]
        : []),
    ],
  }
}

const isFinalReviewStatus = (
  value: unknown,
): value is TransactionFinalApprovalStatus =>
  Object.values(TransactionFinalApprovalStatus).includes(
    value as TransactionFinalApprovalStatus,
  )

const FINAL_REVIEW_STATUS_TEXT: Record<
  TransactionFinalApprovalStatus,
  LocalizedText
> = {
  [TransactionFinalApprovalStatus.Flagged]: localized('Rejected'),
  [TransactionFinalApprovalStatus.PendingReview]: localized('Pending Approval'),
  [TransactionFinalApprovalStatus.Reviewed]: localized('Approved'),
}

const getFinalReviewPresentation = (
  payload: Record<string, unknown>,
): TransactionActivityPresentation | undefined => {
  if (
    !isFinalReviewStatus(payload.before) ||
    !isFinalReviewStatus(payload.after)
  ) {
    return undefined
  }

  const finalPayload: FinalReviewUpdatedPayload = {
    before: payload.before,
    after: payload.after,
  }

  return {
    title: localized('Updated final review:'),
    icon: 'approval',
    supported: true,
    details: [
      {
        kind: 'status',
        value:
          FINAL_REVIEW_STATUS_TEXT[
            finalPayload.after as TransactionFinalApprovalStatus
          ],
      },
    ],
  }
}

export const getTransactionActivityPresentation = (
  activity: TransactionActivity,
): TransactionActivityPresentation => {
  if (activity.schemaVersion !== 1 || !isObject(activity.payload)) {
    return genericPresentation()
  }

  if (activity.type === 'receipt.attached') {
    return simplePresentation(
      activity.payload.attachmentMethod === 'AUTOMATCH'
        ? 'Matched receipt to this transaction'
        : 'Attached a receipt',
      'receipt',
    )
  }

  if (activity.type === 'receipt.chase.sent') {
    return simplePresentation('Sent a receipt reminder', 'receipt')
  }

  if (activity.type === 'receipt.removed') {
    return simplePresentation(
      activity.payload.reason === 'REPLACED'
        ? 'Moved receipt'
        : 'Removed receipt',
      'receipt',
    )
  }

  const presentation =
    activity.type === 'transaction.note.updated'
      ? getNotePresentation(activity.payload)
      : activity.type === 'conversation.message.posted'
        ? getConversationMessagePresentation(activity.payload)
        : activity.type === 'expense.categories.updated'
          ? getCategoryPresentations(activity.payload)
          : activity.type === 'expense.custom_fields.updated'
            ? getCustomFieldPresentations(activity.payload)
            : activity.type === 'approval.manager.updated'
              ? getManagerReviewPresentation(activity.payload)
              : activity.type === 'approval.final.updated'
                ? getFinalReviewPresentation(activity.payload)
                : undefined

  if (presentation) {
    return presentation
  }

  if (!isLifecycleType(activity.type)) {
    return genericPresentation()
  }

  const lifecyclePresentation = LIFECYCLE_PRESENTATIONS[activity.type]

  if (activity.payload.status !== lifecyclePresentation.status) {
    return genericPresentation()
  }

  const money = getMoney(activity.payload)

  return {
    title: localized(lifecyclePresentation.title),
    icon: lifecyclePresentation.icon,
    supported: true,
    details: [],
    ...(activity.type === 'transaction.authorized' && money ? { money } : {}),
  }
}

export const getActorInitials = (displayName: string): string => {
  const initials = displayName
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((name) => name.charAt(0))
    .join('')
    .toUpperCase()

  return initials || '?'
}
